import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type OutletResource,
  type UpiDeclarationResource,
  UpiDeclarationsController_declare_v1,
  type UpiDeclarationsController_declare_v1Body,
  UpiDeclarationsController_read_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { priceInRupeesSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatInstant } from '@/lib/instant';
import { formatPaise, paiseFromRupees } from '@/lib/money';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { DayField, OutletField } from '../billing-machines/machine-fields';
import { isRetired } from '../outlets/outlet-kinds';
import { currentBusinessDate } from '../reporting/reporting-period';
import { formatBusinessDate } from '../tickets/business-date';

/**
 * Declaring the UPI an outlet took on a trading day:
 * `POST /v1/outlets/{outletId}/upi-declarations`, owner and manager.
 *
 * **A declaration is never edited.** A second figure for the same outlet-day
 * is a new declaration naming the current one in `correctsDeclarationId`, and
 * the API refuses anything else:
 *
 * - `REPORTING_UPI_ALREADY_DECLARED` — a plain declaration for a day that has
 *   one;
 * - `REPORTING_UPI_DECLARATION_SUPERSEDED` — a correction naming a figure that
 *   somebody else corrected first.
 *
 * Both carry the one that stands now in `details.currentDeclarationId`, so this
 * dialog turns either refusal into an offer to correct *that* one with the
 * figure already typed — nobody retypes anything, and nobody is sent looking
 * for an id. The standing figure is read back so the person can see what they
 * are replacing.
 */

/** `maximum: 1000000000` on `DeclareUpiDto.amountPaise`, published. */
const MAX_PAISE = 1_000_000_000;

const OUTLET_REQUIRED = 'Choose the outlet that took the UPI.';
const DAY_REQUIRED = 'Choose the trading day the UPI was received on.';
const AMOUNT_TOO_LARGE = `A day’s UPI is at most ${formatPaise(MAX_PAISE)}. Check the figure.`;

const declareSchema = z.object({
  outletId: z.string().min(1, OUTLET_REQUIRED),
  businessDate: z.string().min(1, DAY_REQUIRED),
  // Zero is a real answer — an outlet that took no UPI — so the price fragment,
  // which allows it, with the route's own ceiling on top.
  amount: priceInRupeesSchema.refine(
    (rupees) => paiseFromRupees(rupees) <= MAX_PAISE,
    AMOUNT_TOO_LARGE,
  ),
});

type DeclareValues = z.infer<typeof declareSchema>;

/** A refusal naming the figure that stands, turned into an offer to correct it. */
interface CorrectionOffer {
  readonly message: string;
  /** `details.currentDeclarationId`: what the correction will name. */
  readonly currentId: string;
  /** The standing figure, when it could be read back. */
  readonly current: UpiDeclarationResource | null;
  /** What was typed when refused — the correction sends exactly this. */
  readonly values: DeclareValues;
}

export function DeclareUpiDialog({
  outlets,
  correcting,
  onClose,
  onDeclared,
  onStale,
}: {
  /** Every outlet this screen read, retired included, so a row's is named. */
  outlets: readonly OutletResource[];
  /** The row being corrected, or `null` for a new declaration. */
  correcting: UpiDeclarationResource | null;
  onClose: () => void;
  onDeclared: (declaration: UpiDeclarationResource) => void;
  /** The list behind is out of date: somebody declared or corrected first. */
  onStale: () => void;
}) {
  const outlet = outlets.find((candidate) => candidate.id === correcting?.outletId);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>{correcting === null ? 'Declare UPI' : 'Correct UPI'}</DialogTitle>
          {correcting !== null && (
            <DialogDescription>Replaces the figure below. Both are kept.</DialogDescription>
          )}
        </DialogHeader>

        {correcting !== null && (
          <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
            {outlet?.name ?? 'This outlet'}, {formatBusinessDate(correcting.businessDate)} —{' '}
            <span className="font-mono numeric">{formatPaise(correcting.amountPaise)}</span>{' '}
            declared now
          </p>
        )}

        <DeclareUpiForm
          outlets={outlets.filter((candidate) => !isRetired(candidate))}
          correcting={correcting}
          onDeclared={onDeclared}
          onStale={onStale}
        />
      </FormDialogContent>
    </Dialog>
  );
}

function DeclareUpiForm({
  outlets,
  correcting,
  onDeclared,
  onStale,
}: {
  outlets: readonly OutletResource[];
  correcting: UpiDeclarationResource | null;
  onDeclared: (declaration: UpiDeclarationResource) => void;
  onStale: () => void;
}) {
  const client = useApiClient();
  const today = currentBusinessDate(systemClock);

  const form = useForm({
    resolver: zodResolver(declareSchema),
    mode: 'onTouched',
    defaultValues: {
      outletId: correcting?.outletId ?? '',
      businessDate: correcting?.businessDate ?? today,
      amount: '',
    },
  });

  const [formError, setFormError] = useState<string | null>(null);
  const [offer, setOffer] = useState<CorrectionOffer | null>(null);

  // Per body — see `lib/write-attempt.ts`. A correction after a refusal is a
  // different body, so it gets a fresh key; a retry of the same one replays.
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;

  const send = async (values: DeclareValues, correctsDeclarationId: string | null) => {
    setFormError(null);
    setOffer(null);

    const body: UpiDeclarationsController_declare_v1Body = {
      businessDate: values.businessDate,
      amountPaise: paiseFromRupees(values.amount),
      ...(correctsDeclarationId === null ? {} : { correctsDeclarationId }),
    };
    const sending = attemptFor(attempt.current, JSON.stringify([values.outletId, body]));
    attempt.current = sending;

    try {
      onDeclared(
        await client.request(UpiDeclarationsController_declare_v1, {
          path: { outletId: values.outletId },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error) {
      const code = error instanceof ApiError ? error.code : undefined;
      const currentId = error instanceof ApiError ? error.details?.['currentDeclarationId'] : null;
      const correctable =
        code === 'REPORTING_UPI_ALREADY_DECLARED' ||
        code === 'REPORTING_UPI_DECLARATION_SUPERSEDED';

      if (correctable && typeof currentId === 'string') {
        onStale();
        setOffer({
          message: messageForFailure(error),
          currentId,
          current: await standingFigure(values, currentId),
          values,
        });

        return;
      }

      setFormError(messageForFailure(error));
    }
  };

  /** The figure the refusal named, or `null` if it cannot be read back now. */
  const standingFigure = async (
    values: DeclareValues,
    currentId: string,
  ): Promise<UpiDeclarationResource | null> => {
    try {
      const current = await client.request(UpiDeclarationsController_read_v1, {
        path: { outletId: values.outletId, businessDate: values.businessDate },
      });

      // Somebody may have corrected it again in between; then this is not the
      // figure the offer names, and saying it was would be wrong.
      return current.id === currentId ? current : null;
    } catch {
      return null;
    }
  };

  const handleSubmit = async (values: DeclareValues) => {
    if (pending) return;

    await send(values, correcting?.id ?? null);
  };

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        {/* A correction is of one outlet-day, so neither can move. */}
        {correcting === null && (
          <>
            <OutletField control={form.control} name="outletId" outlets={outlets} idPrefix="upi" />

            <DayField
              control={form.control}
              name="businessDate"
              label="Trading day"
              maxDay={today}
              idPrefix="upi"
            />
          </>
        )}

        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                UPI received, in rupees
              </FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11 font-mono numeric`}
                    required
                    inputMode="decimal"
                    placeholder="12540.50"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormDescription>The day’s whole figure. 0 is allowed.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {offer !== null && (
          <Alert>
            <IconAlertTriangle />
            <AlertDescription className="space-y-2">
              <p>{offer.message}</p>
              <p>
                {offer.current === null
                  ? 'A figure already stands for that day.'
                  : `Standing now: ${formatPaise(offer.current.amountPaise)}, declared ${formatInstant(offer.current.recordedAt)}.`}{' '}
                Correcting it records {formatPaise(paiseFromRupees(offer.values.amount))} in its
                place.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          {offer === null ? (
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : correcting === null ? 'Declare' : 'Record correction'}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={pending}
              onClick={() => void form.handleSubmit(() => send(offer.values, offer.currentId))()}
            >
              {pending ? 'Saving…' : 'Correct the current figure'}
            </Button>
          )}
        </DialogFooter>
      </form>
    </Form>
  );
}
